import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

interface RoadmapPhase {
  phase: string;
  title: string;
  status: 'completed' | 'in_progress' | 'upcoming';
  items: string[];
}

const roadmapData: RoadmapPhase[] = [
  {
    phase: 'Fase 1',
    title: 'Lanzamiento PokTaPok',
    status: 'completed',
    items: [
      'Lanzamiento de la plataforma web',
      'Registro de usuarios y canchas',
      'Sistema de busqueda y filtros',
      'Reservas con pago tradicional',
      'Sistema de calificaciones y reseñas'
    ]
  },
  {
    phase: 'Fase 2',
    title: 'PokTaToken',
    status: 'completed',
    items: [
      'Creacion del token en blockchain',
      'Integracion con wallets (MetaMask, Trust)',
      'Sistema de recompensas por reservas',
      'Programa de referidos con tokens',
      'Cashback del 5% en PokTaToken'
    ]
  },
  {
    phase: 'Fase 3',
    title: 'Asistente de IA',
    status: 'in_progress',
    items: [
      'Chatbot inteligente para reservas',
      'Atencion al cliente 24/7',
      'Busqueda por voz y texto',
      'Recomendaciones personalizadas',
      'Integracion con calendario'
    ]
  },
  {
    phase: 'Fase 4',
    title: 'App Movil',
    status: 'upcoming',
    items: [
      'Aplicacion iOS',
      'Aplicacion Android',
      'Notificaciones push',
      'Reservas con un solo toque',
      'Geolocalizacion en tiempo real'
    ]
  },
  {
    phase: 'Fase 5',
    title: 'Liga PokTaPok',
    status: 'upcoming',
    items: [
      'Organizacion de ligas y torneos',
      'Sistema de estadisticas',
      'Ranking de jugadores',
      'Premios en PokTaToken',
      'Transmisiones en vivo'
    ]
  },
  {
    phase: 'Fase 6',
    title: 'Ecosistema Completo',
    status: 'upcoming',
    items: [
      'Mercado de equipamiento deportivo',
      'Alquiler de equipo con tokens',
      'Entrenadores personales',
      'Seguros deportivos',
      'Integracion con otros deportes'
    ]
  }
];

export default function Roadmap() {
  const navigate = useNavigate();
  const [activePhase, setActivePhase] = useState(2);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-600 text-white';
      case 'in_progress':
        return 'bg-yellow-500 text-white';
      default:
        return 'bg-gray-200 text-gray-600';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completado';
      case 'in_progress':
        return 'En Progreso';
      default:
        return 'Proximamente';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return 'ri-check-line';
      case 'in_progress':
        return 'ri-loader-4-line animate-spin';
      default:
        return 'ri-time-line';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-green-900 to-green-800 text-white py-20 md:py-28">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Abstract%20roadmap%20timeline%20visualization%20with%20connected%20nodes%20and%20glowing%20paths%20on%20dark%20background%2C%20futuristic%20progress%20chart%20with%20green%20and%20gold%20accents%2C%20digital%20milestone%20markers%20on%20a%20curved%20path%2C%20minimal%20dark%20background%20with%20geometric%20elements&width=1600&height=900&seq=roadmap-bg&orientation=landscape')] bg-cover bg-center"></div>
        </div>
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
              <i className="ri-map-line text-yellow-300"></i>
              <span className="text-sm font-medium text-yellow-100">Exclusivo para Usuarios</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Roadmap PokTaPok
            </h1>
            <p className="text-xl md:text-2xl text-green-100 mb-10 leading-relaxed">
              El plan de desarrollo de PokTaPok. Descubre que hemos logrado, 
              que estamos construyendo y hacia donde vamos.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                <span className="text-green-100">Completado</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                <span className="text-green-100">En Progreso</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-gray-400"></span>
                <span className="text-green-100">Proximamente</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Desktop Timeline */}
            <div className="hidden md:block">
              <div className="relative">
                {/* Line */}
                <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gray-200 -translate-x-1/2"></div>

                {roadmapData.map((phase, index) => (
                  <div
                    key={index}
                    className={`relative flex items-center mb-12 ${
                      index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                    }`}
                  >
                    {/* Content */}
                    <div className={`w-5/12 ${index % 2 === 0 ? 'pr-12 text-right' : 'pl-12 text-left'}`}>
                      <div
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-3 ${
                          getStatusColor(phase.status)
                        }`}
                      >
                        <i className={getStatusIcon(phase.status)}></i>
                        {getStatusText(phase.status)}
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-1">{phase.phase}</h3>
                      <h4 className="text-lg font-semibold text-green-700 mb-3">{phase.title}</h4>
                      <ul className="space-y-1">
                        {phase.items.map((item, i) => (
                          <li key={i} className="text-gray-600 text-sm flex items-start gap-2">
                            <i className="ri-check-line text-green-500 mt-0.5 shrink-0"></i>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Center Dot */}
                    <div className="w-2/12 flex justify-center">
                      <div
                        className={`w-6 h-6 rounded-full border-4 z-10 ${
                          phase.status === 'completed'
                            ? 'bg-green-600 border-green-600'
                            : phase.status === 'in_progress'
                            ? 'bg-yellow-500 border-yellow-500'
                            : 'bg-white border-gray-300'
                        }`}
                      ></div>
                    </div>

                    {/* Spacer */}
                    <div className="w-5/12"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Timeline */}
            <div className="md:hidden">
              <div className="relative pl-8">
                {/* Line */}
                <div className="absolute left-3 top-0 bottom-0 w-1 bg-gray-200"></div>

                {roadmapData.map((phase, index) => (
                  <div key={index} className="relative mb-8">
                    {/* Dot */}
                    <div
                      className={`absolute left-0 w-7 h-7 rounded-full border-4 -translate-x-1/2 top-1 ${
                        phase.status === 'completed'
                          ? 'bg-green-600 border-green-600'
                          : phase.status === 'in_progress'
                          ? 'bg-yellow-500 border-yellow-500'
                          : 'bg-white border-gray-300'
                      }`}
                    ></div>

                    <div className="ml-4">
                      <div
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-2 ${
                          getStatusColor(phase.status)
                        }`}
                      >
                        <i className={getStatusIcon(phase.status)}></i>
                        {getStatusText(phase.status)}
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-1">{phase.phase}</h3>
                      <h4 className="text-lg font-semibold text-green-700 mb-2">{phase.title}</h4>
                      <ul className="space-y-1">
                        {phase.items.map((item, i) => (
                          <li key={i} className="text-gray-600 text-sm flex items-start gap-2">
                            <i className="ri-check-line text-green-500 mt-0.5 shrink-0"></i>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Phase Cards */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Detalles por Fase
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Selecciona una fase para ver los detalles completos
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {roadmapData.map((phase, index) => (
              <button
                key={index}
                onClick={() => setActivePhase(index)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activePhase === index
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                {phase.phase}
              </button>
            ))}
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div
                  className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                    getStatusColor(roadmapData[activePhase].status)
                  }`}
                >
                  <i className={getStatusIcon(roadmapData[activePhase].status)}></i>
                  {getStatusText(roadmapData[activePhase].status)}
                </div>
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-2">
                {roadmapData[activePhase].phase}
              </h3>
              <h4 className="text-xl font-semibold text-green-700 mb-6">
                {roadmapData[activePhase].title}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {roadmapData[activePhase].items.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                      <i className="ri-check-line text-green-600"></i>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-green-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Se parte del futuro
          </h2>
          <p className="text-green-100 text-lg max-w-2xl mx-auto mb-10">
            Crea tu cuenta y empieza a usar PokTaPok. Gana PokTaToken, 
            accede a funciones exclusivas y ayudanos a construir el futuro del futbol.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-green-800 hover:bg-gray-100"
              onClick={() => navigate('/registro')}
            >
              <i className="ri-user-add-line mr-2"></i>
              Crear Cuenta
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-green-800"
              onClick={() => navigate('/poktatoken')}
            >
              <i className="ri-coins-line mr-2"></i>
              Conocer PokTaToken
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}